import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo, useRef, useState } from "react";

import StepBar from "./StepBar";

const sameId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

const STATUT_OPTIONS = [
  { label: "À faire", value: "a_faire" },
  { label: "En cours", value: "en_cours" },
  { label: "Réalisée", value: "realisee" },
];

const isValidDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const fieldLabelSx = {
  color: "#536b91",
  display: "block",
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: "0.06em",
  mb: 0.55,
  textTransform: "uppercase",
  "& .MuiFormLabel-asterisk": { color: "#d32f2f" },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "8px",
    fontSize: 13,
  },
};

const CuratifStep = ({
  canSkip,
  formData,
  isLocked,
  navigationEnabled,
  onBack,
  onChange,
  onNext,
  onRetryUsers,
  onSave,
  onStepChange,
  saving,
  users,
  usersError,
  usersLoading,
}) => {
  const [errors, setErrors] = useState({});
  const descriptionRef = useRef(null);
  const responsableRef = useRef(null);
  const dateRef = useRef(null);
  const statutRef = useRef(null);

  const selectedResponsable = useMemo(
    () =>
      users.find((item) => sameId(item.id, formData.action_curative_responsable)) ||
      null,
    [formData.action_curative_responsable, users]
  );

  const updateField = (name, value) => {
    onChange(name, value);
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!String(formData.action_curative_description || "").trim()) {
      nextErrors.action_curative_description =
        "La description de l’action curative est obligatoire.";
    }
    const responsableId = Number(formData.action_curative_responsable);
    if (!Number.isInteger(responsableId) || responsableId < 1) {
      nextErrors.action_curative_responsable = "Le responsable est obligatoire.";
    }
    if (!formData.action_curative_date) {
      nextErrors.action_curative_date = "La date de réalisation est obligatoire.";
    } else if (!isValidDate(formData.action_curative_date)) {
      nextErrors.action_curative_date = "La date de réalisation est invalide.";
    }
    if (
      !STATUT_OPTIONS.some(
        (option) => option.value === formData.action_curative_statut
      )
    ) {
      nextErrors.action_curative_statut = "Le statut est obligatoire.";
    }

    setErrors(nextErrors);

    if (nextErrors.action_curative_description) descriptionRef.current?.focus();
    else if (nextErrors.action_curative_responsable) responsableRef.current?.focus();
    else if (nextErrors.action_curative_date) dateRef.current?.focus();
    else if (nextErrors.action_curative_statut) {
      statutRef.current?.querySelector("input")?.focus();
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = (destination) => {
    if (!saving && !isLocked && validate()) onSave(destination);
  };

  const handleNext = () => {
    if (saving || isLocked) return;

    const isEmpty =
      !String(formData.action_curative_description || "").trim() &&
      !formData.action_curative_responsable &&
      !formData.action_curative_date &&
      formData.action_curative_statut === "a_faire";

    if (canSkip && isEmpty) {
      setErrors({});
      onNext();
      return;
    }

    if (validate()) onSave("analyse");
  };

  return (
    <Box
      aria-label="Étape action curative immédiate"
      role="region"
      sx={{
        backgroundColor: "#f1f5fb",
        minHeight: "calc(100vh - 56px)",
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <StepBar
        activeStep={2}
        navigationEnabled={navigationEnabled}
        onStepChange={onStepChange}
      />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #cbd8e6",
          borderRadius: "12px",
          mt: 2.5,
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            borderBottom: "1px solid #d5e0eb",
            display: "flex",
            gap: 1.5,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: "#fff3dc",
              borderRadius: "9px",
              color: "#8a5100",
              display: "flex",
              height: 34,
              justifyContent: "center",
              width: 34,
            }}
          >
            <BuildOutlinedIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box>
            <Typography color="#10263d" fontSize={14} fontWeight={800}>
              Étape 3 — Action curative immédiate
            </Typography>
            <Typography color="#60758a" fontSize={11}>
              Correction rapide pour limiter les effets aujourd&apos;hui
            </Typography>
          </Box>
        </Box>

        <Alert
          icon={<span aria-hidden="true">💡</span>}
          severity="warning"
          sx={{
            backgroundColor: "#fffbed",
            border: "1px solid #f2ce55",
            color: "#8a5100",
            mt: 2,
          }}
        >
          <strong>Action curative ≠ Action corrective.</strong> L&apos;action
          curative corrige l&apos;effet immédiat. L&apos;action corrective élimine
          la cause (étape 5).
        </Alert>

        {isLocked && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Cette NC est clôturée. Son action curative ne peut plus être modifiée.
          </Alert>
        )}

        <Grid container spacing={1.7} sx={{ mt: 0.2 }}>
          <Grid item xs={12}>
            <FormLabel
              id="action-curative-description-label"
              required
              sx={fieldLabelSx}
            >
              Description de l&apos;action curative
            </FormLabel>
            <Box
              aria-invalid={Boolean(errors.action_curative_description)}
              aria-labelledby="action-curative-description-label"
              component="textarea"
              disabled={isLocked || saving}
              onChange={(event) =>
                updateField("action_curative_description", event.target.value)
              }
              placeholder="Que faites-vous immédiatement pour limiter l'impact ?"
              ref={descriptionRef}
              rows={4}
              sx={{
                backgroundColor: "#fff",
                border: `1px solid ${
                  errors.action_curative_description ? "#d32f2f" : "#c7d3e4"
                }`,
                borderRadius: "8px",
                boxSizing: "border-box",
                color: "#10263d",
                fontFamily: "inherit",
                fontSize: 13,
                lineHeight: 1.5,
                minHeight: 110,
                outline: "none",
                p: 1.4,
                resize: "vertical",
                transition: "border-color 150ms ease, box-shadow 150ms ease",
                width: "100%",
                "&:hover": { borderColor: "#7890aa" },
                "&:focus": {
                  borderColor: errors.action_curative_description
                    ? "#d32f2f"
                    : "#00a6b6",
                  boxShadow: errors.action_curative_description
                    ? "0 0 0 2px rgba(211, 47, 47, 0.14)"
                    : "0 0 0 2px rgba(0, 166, 182, 0.16)",
                },
                "&::placeholder": { color: "#8a9aae", opacity: 1 },
              }}
              value={formData.action_curative_description}
            />
            <Typography
              color={
                errors.action_curative_description ? "#d32f2f" : "transparent"
              }
              fontSize={11}
              sx={{ minHeight: 18, mt: 0.4 }}
            >
              {errors.action_curative_description || "Aucune erreur"}
            </Typography>
          </Grid>

          <Grid item md={4} xs={12}>
            <FormLabel required sx={fieldLabelSx}>
              Responsable
            </FormLabel>
            <Autocomplete
              disabled={isLocked || saving || usersLoading || Boolean(usersError)}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
              loading={usersLoading}
              loadingText="Chargement des utilisateurs…"
              noOptionsText="Aucun utilisateur actif"
              onChange={(_, option) =>
                updateField("action_curative_responsable", option?.id ?? "")
              }
              options={users}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(usersError || errors.action_curative_responsable)}
                  helperText={
                    errors.action_curative_responsable || usersError || " "
                  }
                  inputRef={responsableRef}
                  placeholder="Qui agit ?"
                  size="small"
                  sx={fieldSx}
                />
              )}
              value={selectedResponsable}
            />
            {usersError && (
              <Button
                onClick={onRetryUsers}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Réessayer
              </Button>
            )}
          </Grid>

          <Grid item md={4} xs={12}>
            <FormLabel required sx={fieldLabelSx}>
              Date de réalisation
            </FormLabel>
            <TextField
              disabled={isLocked || saving}
              error={Boolean(errors.action_curative_date)}
              fullWidth
              helperText={errors.action_curative_date || " "}
              inputProps={{ max: "9999-12-31" }}
              inputRef={dateRef}
              onChange={(event) =>
                updateField("action_curative_date", event.target.value)
              }
              size="small"
              sx={fieldSx}
              type="date"
              value={formData.action_curative_date}
            />
          </Grid>

          <Grid item md={4} xs={12}>
            <FormLabel required sx={fieldLabelSx}>
              Statut
            </FormLabel>
            <RadioGroup
              aria-label="Statut de l'action curative"
              onChange={(event) =>
                updateField("action_curative_statut", event.target.value)
              }
              ref={statutRef}
              row
              sx={{
                gap: 1,
                minHeight: 40,
                "& .MuiFormControlLabel-root": {
                  border: "1px solid #cbd8e6",
                  borderRadius: "18px",
                  m: 0,
                  pr: 1.4,
                },
                "& .MuiFormControlLabel-label": {
                  color: "#10263d",
                  fontSize: 12,
                  fontWeight: 800,
                },
                "& .MuiRadio-root": { p: 0.7 },
              }}
              value={formData.action_curative_statut}
            >
              {STATUT_OPTIONS.map((option) => (
                <FormControlLabel
                  control={<Radio size="small" />}
                  disabled={isLocked || saving}
                  key={option.value}
                  label={option.label}
                  value={option.value}
                />
              ))}
            </RadioGroup>
            <Typography
              color={errors.action_curative_statut ? "#d32f2f" : "transparent"}
              fontSize={11}
              sx={{ minHeight: 18, mt: 0.4 }}
            >
              {errors.action_curative_statut || "Aucune erreur"}
            </Typography>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "flex-end",
            mt: 2.5,
          }}
        >
          <Button
            disabled={saving}
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: "none" }}
            variant="outlined"
          >
            Retour
          </Button>
          <Button
            disabled={saving || isLocked}
            onClick={() => handleSave("registre")}
            startIcon={
              saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />
            }
            sx={{
              backgroundColor: "#00a6b6",
              textTransform: "none",
              "&:hover": { backgroundColor: "#008f9d" },
            }}
            variant="contained"
          >
            Enregistrer la NC
          </Button>
          <Button
            disabled={saving || isLocked}
            endIcon={saving ? null : <ArrowForwardIcon />}
            onClick={handleNext}
            sx={{
              backgroundColor: "#00a6b6",
              textTransform: "none",
              "&:hover": { backgroundColor: "#008f9d" },
            }}
            variant="contained"
          >
            Étape suivante
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

CuratifStep.defaultProps = {
  navigationEnabled: false,
  onStepChange: () => {},
  usersError: null,
};

CuratifStep.propTypes = {
  canSkip: PropTypes.bool.isRequired,
  formData: PropTypes.shape({
    action_curative_date: PropTypes.string,
    action_curative_description: PropTypes.string,
    action_curative_responsable: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    action_curative_statut: PropTypes.string,
  }).isRequired,
  isLocked: PropTypes.bool.isRequired,
  navigationEnabled: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onRetryUsers: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onStepChange: PropTypes.func,
  saving: PropTypes.bool.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  usersError: PropTypes.string,
  usersLoading: PropTypes.bool.isRequired,
};

export default CuratifStep;
